import React, { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaSpinner, FaSave, FaImage, FaBox, FaArrowLeft, FaEdit, FaStar } from "react-icons/fa";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function EditProductPage() {
    const [productData, setProductData] = useState(null);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [parentCategories, setParentCategories] = useState([]);

    const [brandSearchTerm, setBrandSearchTerm] = useState('');
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const [parentCategorySearchTerm, setParentCategorySearchTerm] = useState('');
    const [showParentCategoryDropdown, setShowParentCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showUnitDropdown, setShowUnitDropdown] = useState(false);

    const filteredBrandsForSelect = brands.filter(b =>
        (b.name || '').toLowerCase().includes(brandSearchTerm.toLowerCase())
    );

    const filteredParentCategoriesForSelect = parentCategories.filter(pc =>
        (pc.name || '').toLowerCase().includes(parentCategorySearchTerm.toLowerCase())
    );

    const filteredCategoriesForSelect = categories.filter(c =>
        (c.name || '').toLowerCase().includes(categorySearchTerm.toLowerCase())
    );

    const standardUnits = ['kg', 'g', 'pcs', 'pc', 'packet', 'packets', 'box', 'ml', 'l', 'bottle', 'pack', 'bag', 'pouch', 'dozen', 'piece', 'pieces', 'bundle', 'roll', 'set', 'carton', 'crate', 'can'];
    const filteredUnitsForSelect = standardUnits.filter(u =>
        u.toLowerCase().includes((productData?.unit || '').toLowerCase())
    );
    // Fetch parent categories
    const getParentCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/parent-categories`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setParentCategories(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };
    const { productId } = useParams();
    const [uploadingStates, setUploadingStates] = useState({
        image1: false,
        image2: false,
        image3: false,
        image4: false,
        thumbnail: false,
        video: false,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/categorydata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getBrands = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/branddata`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data);
        } catch (error) {
            handleApiError(error);
        }
    };

    const getProductData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/productdata/${productId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const product = response.data.productData;
            const normalizedBrand = product.brand?._id || product.brand || '';
            setProductData({
                ...product,
                brand: normalizedBrand,
            });
            setBrandSearchTerm(normalizedBrand);
            setParentCategorySearchTerm(product.parentCategory || '');
            setCategorySearchTerm(product.category || '');
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handleReplaceImage = async (event) => {
        const imageKey = event.target.name;
        const file = event.target.files[0];
        if (!file) return;

        if (!['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(file.type)) {
            alert('Only JPG, PNG, WebP, or AVIF images are allowed.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setUploadingStates((prev) => ({ ...prev, [imageKey]: true }));
            const formData = new FormData();
            formData.append('image', file);
            formData.append('imageKey', imageKey);
            formData.append('productId', productId);
            const response = await axios.post(`${API_BASE_URL}/replace-img`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProductData((prev) => ({ ...prev, [imageKey]: response.data.imagePath }));
            alert("Image updated successfully!");
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadingStates((prev) => ({ ...prev, [imageKey]: false }));
        }
    };

    const handleReplaceVideo = async (event) => {
        const videoKey = event.target.name; // expecting "video"
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only MP4, WebM, or OGG videos are allowed.');
            return;
        }
        if (file.size > 50 * 1024 * 1024) {
            alert('Video size must be under 50MB.');
            return;
        }

        try {
            setUploadingStates((prev) => ({ ...prev, [videoKey]: true }));
            const formData = new FormData();
            formData.append('video', file);
            const response = await axios.post(`${API_BASE_URL}/upload-video`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProductData((prev) => ({ ...prev, [videoKey]: response.data.path }));
            alert('Video uploaded successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setUploadingStates((prev) => ({ ...prev, [videoKey]: false }));
        }
    };

    const handleChange = (event) => {
        setProductData({
            ...productData,
            [event.target.name]: event.target.value,
        });
    };

    // Custom star rating handler
    const handleStarClick = (starValue) => {
        setProductData((prevData) => ({
            ...prevData,
            rating: starValue
        }));
    };

    const validateFields = () => {
        const requiredFields = [
            'name', 'brand', 'originalPrice', 'price', 'category', 'quantity', 'unit', 'availableStock', 'description',
            'image1', 'image2'
        ];

        for (let field of requiredFields) {
            if (!productData || !productData[field] || productData[field].toString().trim() === '') {
                alert(`${field.charAt(0).toUpperCase() + field.slice(1)} is required`);
                return false;
            }
        }
        if (Number(productData.price) > Number(productData.originalPrice)) {
            alert('Price cannot be greater than Original Price.');
            return false;
        }
        if (Number(productData.availableStock) < 0 || Number.isNaN(Number(productData.availableStock))) {
            alert('Stock must be a non-negative number.');
            return false;
        }
        return true;
    };

    const handleUpdateProduct = async () => {
        if (!validateFields()) return;

        // Prepare product data, process tags as array
        let dataToUpdate = { ...productData };
        if (typeof dataToUpdate.tags === 'string') {
            dataToUpdate.tags = dataToUpdate.tags
                .split(',')
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0);
        }

        try {
            setSaving(true);
            const response = await axios.patch(`${API_BASE_URL}/update-product`, dataToUpdate, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate(-1);
        } catch (error) {
            handleApiError(error);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        getCategories();
        getProductData();
        getBrands();
        getParentCategories();
    }, []);

    if (loading) {
        return (
            <div className="container-fluid p-0 g-0 adma" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
                <div className="row p-3 pb-2 g-0" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1" style={{ fontSize: '16px', fontWeight: '700' }}>EDIT PRODUCT</h4>
                            <p className="text-muted p-0 m-0" style={{ fontSize: '12px' }}>Update product information and images</p>
                        </div>
                    </div>
                </div>
                <div className="p-3">
                    <AdminPageLoader message="Loading product details..." />
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-0 g-0 adma" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
                {/* Header */}
                <div className="row p-3 pb-2 g-0" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1" style={{ fontSize: '16px', fontWeight: '700' }}>EDIT PRODUCT</h4>
                                <p className="text-muted p-0 m-0" style={{ fontSize: '12px' }}>Update product information and images</p>
                            </div>
                            <button 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                onClick={() => navigate(-1)}
                                title="Back to Products"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FaArrowLeft size={11} />
                                Back to Products
                            </button>
                        </div>
                    </div>
                </div>

                {/* Product Form */}
                <div className="row g-0 details-content-row">
                    <div className="col-12">
                        <div className="card rounded-0" style={{ border: 'none', borderBottom: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <div className="card-header rounded-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px', margin: 0, display: 'flex', alignItems: 'center' }}>
                                <h5 className="mb-0" style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', margin: 0 }}>
                                    <FaEdit className="me-2" style={{ fontSize: '14px' }} />
                                    Product Information
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    {/* Left Column - Basic Info */}
                                    <div className="col-md-6 p-4">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Product Name</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="name"
                                                value={productData?.name || ''}
                                                onChange={handleChange}
                                                placeholder="Enter product name"
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Brand</label>
                                            <div className="position-relative">
                                                <input 
                                                    type="text"
                                                    className="form-control rounded-0"
                                                    style={{ border: '1px solid #cbd5e1', fontSize: '13px', height: '38px' }}
                                                    placeholder="Search & select brand..."
                                                    value={showBrandDropdown ? brandSearchTerm : (productData?.brand || '')}
                                                    onFocus={() => {
                                                        setBrandSearchTerm('');
                                                        setShowBrandDropdown(true);
                                                    }}
                                                    onChange={(e) => setBrandSearchTerm(e.target.value)}
                                                    onBlur={() => setTimeout(() => setShowBrandDropdown(false), 250)}
                                                />
                                                {showBrandDropdown && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        background: '#ffffff',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: 0,
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        zIndex: 20000,
                                                        marginTop: '4px'
                                                    }}>
                                                        {filteredBrandsForSelect.length > 0 ? (
                                                            filteredBrandsForSelect.map((brand) => (
                                                                <div
                                                                    key={brand._id}
                                                                    style={{
                                                                        padding: '8px 12px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '13px',
                                                                        color: '#334155',
                                                                        transition: 'background 0.15s'
                                                                    }}
                                                                    onMouseDown={() => {
                                                                        setProductData(prev => ({ ...prev, brand: brand.name }));
                                                                        setBrandSearchTerm(brand.name);
                                                                        setShowBrandDropdown(false);
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    {brand.name}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                                No brands found
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Parent Category</label>
                                            <div className="position-relative">
                                                <input 
                                                    type="text"
                                                    className="form-control rounded-0"
                                                    style={{ border: '1px solid #cbd5e1', fontSize: '13px', height: '38px' }}
                                                    placeholder="Search & select parent category..."
                                                    value={showParentCategoryDropdown ? parentCategorySearchTerm : (productData?.parentCategory || '')}
                                                    onFocus={() => {
                                                        setParentCategorySearchTerm('');
                                                        setShowParentCategoryDropdown(true);
                                                    }}
                                                    onChange={(e) => setParentCategorySearchTerm(e.target.value)}
                                                    onBlur={() => setTimeout(() => setShowParentCategoryDropdown(false), 250)}
                                                />
                                                {showParentCategoryDropdown && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        background: '#ffffff',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: 0,
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        zIndex: 20000,
                                                        marginTop: '4px'
                                                    }}>
                                                        {filteredParentCategoriesForSelect.length > 0 ? (
                                                            filteredParentCategoriesForSelect.map((parent) => (
                                                                <div
                                                                    key={parent._id}
                                                                    style={{
                                                                        padding: '8px 12px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '13px',
                                                                        color: '#334155',
                                                                        transition: 'background 0.15s'
                                                                    }}
                                                                    onMouseDown={() => {
                                                                        setProductData(prev => ({ ...prev, parentCategory: parent.name }));
                                                                        setParentCategorySearchTerm(parent.name);
                                                                        setShowParentCategoryDropdown(false);
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    {parent.name}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                                No parent categories found
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Category</label>
                                            <div className="position-relative">
                                                <input 
                                                    type="text"
                                                    className="form-control rounded-0"
                                                    style={{ border: '1px solid #cbd5e1', fontSize: '13px', height: '38px' }}
                                                    placeholder="Search & select category..."
                                                    value={showCategoryDropdown ? categorySearchTerm : (productData?.category || '')}
                                                    onFocus={() => {
                                                        setCategorySearchTerm('');
                                                        setShowCategoryDropdown(true);
                                                    }}
                                                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                                                    onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 250)}
                                                />
                                                {showCategoryDropdown && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        background: '#ffffff',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: 0,
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        zIndex: 20000,
                                                        marginTop: '4px'
                                                    }}>
                                                        {filteredCategoriesForSelect.length > 0 ? (
                                                            filteredCategoriesForSelect.map((category) => (
                                                                <div
                                                                    key={category._id}
                                                                    style={{
                                                                        padding: '8px 12px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '13px',
                                                                        color: '#334155',
                                                                        transition: 'background 0.15s'
                                                                    }}
                                                                    onMouseDown={() => {
                                                                        setProductData(prev => ({ ...prev, category: category.name }));
                                                                        setCategorySearchTerm(category.name);
                                                                        setShowCategoryDropdown(false);
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    {category.name}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                                No categories found
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Original Price</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="originalPrice"
                                                value={productData?.originalPrice || ''}
                                                onChange={handleChange}
                                                placeholder="Enter original price"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Price</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="price"
                                                value={productData?.price || ''}
                                                onChange={handleChange}
                                                placeholder="Enter price"
                                            />
                                        </div>
                                    </div>

                                    {/* Right Column - Quantity, Unit, Star Rating, Description */}
                                    <div className="col-md-6 p-4 border-start" style={{ borderColor: '#e2e8f0' }}>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Quantity</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="quantity"
                                                value={productData?.quantity || ''}
                                                onChange={handleChange}
                                                placeholder="Enter quantity"
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Unit</label>
                                            <div className="position-relative">
                                                <input 
                                                    type="text"
                                                    className="form-control rounded-0"
                                                    style={{ border: '1px solid #cbd5e1', fontSize: '13px', height: '38px' }}
                                                    placeholder="Search, select, or type unit..."
                                                    value={productData?.unit || ''}
                                                    onFocus={() => setShowUnitDropdown(true)}
                                                    onChange={(e) => setProductData(prev => ({ ...prev, unit: e.target.value }))}
                                                    onBlur={() => setTimeout(() => setShowUnitDropdown(false), 250)}
                                                />
                                                {showUnitDropdown && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        right: 0,
                                                        background: '#ffffff',
                                                        border: '1px solid #cbd5e1',
                                                        borderRadius: 0,
                                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                                        maxHeight: '200px',
                                                        overflowY: 'auto',
                                                        zIndex: 20000,
                                                        marginTop: '4px'
                                                    }}>
                                                        {filteredUnitsForSelect.length > 0 ? (
                                                            filteredUnitsForSelect.map((unitOption, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    style={{
                                                                        padding: '8px 12px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '13px',
                                                                        color: '#334155',
                                                                        transition: 'background 0.15s'
                                                                    }}
                                                                    onMouseDown={() => {
                                                                        setProductData(prev => ({ ...prev, unit: unitOption }));
                                                                        setShowUnitDropdown(false);
                                                                    }}
                                                                    onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                                                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                                                >
                                                                    {unitOption}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                                                Press Tab or click away to keep custom unit: "{productData?.unit}"
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Stock</label>
                                            <input
                                                type="number"
                                                min={0}
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="availableStock"
                                                value={productData?.availableStock ?? ''}
                                                onChange={handleChange}
                                                placeholder="Enter available stock"
                                            />
                                        </div>
                                        {/* Star Rating (Custom with react-icons) */}
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Star Rating</label>
                                            <div className="d-flex align-items-center" style={{ height: '38px' }}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <FaStar
                                                        key={star}
                                                        size={22}
                                                        style={{ cursor: 'pointer', color: star <= (productData?.rating || 0) ? '#ffd700' : '#e4e5e9', marginRight: 4 }}
                                                        onClick={() => handleStarClick(star)}
                                                        title={star + ' Star'}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Tags</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                placeholder="Enter tags, separated by commas"
                                                name="tags"
                                                value={Array.isArray(productData?.tags) ? productData.tags.join(', ') : (productData?.tags || '')}
                                                onChange={handleChange}
                                            />
                                            <small className="text-muted" style={{ fontSize: '11px' }}>Example: organic, vegan, summer</small>
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Description</label>
                                            <textarea
                                                rows={4}
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', fontSize: '13px', resize: 'none' }}
                                                name="description"
                                                value={productData?.description || ''}
                                                onChange={handleChange}
                                                placeholder="Enter product description"
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Product Images */}
                <div className="row g-0 details-content-row mt-4">
                    <div className="col-12">
                        <div className="card rounded-0" style={{ border: 'none', borderBottom: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <div className="card-header rounded-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px', margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h5 className="mb-0" style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', margin: 0 }}>
                                    <FaImage className="me-2" style={{ fontSize: '14px' }} />
                                    Product Images
                                </h5>
                                <small style={{ color: '#64748b', fontSize: '11px', fontWeight: '500' }}>*First image will be the cover image*</small>
                            </div>
                            <div className="card-body p-4">
                                <div className="row g-3">
                                    {/* Thumbnail upload */}
                                    <div className="col-md-6 col-lg-3" key="thumbnail">
                                        <div className="border rounded-0 p-3" style={{ background: '#ffffff', borderColor: '#cbd5e1' }}>
                                            <label className="form-label fw-bold mb-2" style={{ fontSize: '12px', color: '#374151' }}>
                                                Thumbnail
                                            </label>
                                            <div className="text-center mb-3">
                                                {uploadingStates.thumbnail ? (
                                                    <div className="d-flex align-items-center justify-content-center" style={{ height: '120px' }}>
                                                        <FaSpinner className="spinner-border spinner-border-sm me-2 text-primary" role="status" style={{ border: 'none', background: 'none' }} />
                                                        <small className="text-muted">Uploading...</small>
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={`${API_BASE_URL}/${productData?.thumbnail}`}
                                                        alt={`${productData?.name} - Thumbnail`}
                                                        className="img-fluid rounded-0"
                                                        style={{ maxHeight: '120px', width: 'auto', border: '1px solid #e2e8f0' }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'block';
                                                        }}
                                                    />
                                                )}
                                                <div className="text-muted small mt-1" style={{ display: 'none', fontSize: '11px' }}>
                                                    No thumbnail available
                                                </div>
                                            </div>
                                            <input
                                                type="file"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', fontSize: '13px', padding: '7px 12px' }}
                                                name="thumbnail"
                                                accept="image/*"
                                                onChange={handleReplaceImage}
                                            />
                                        </div>
                                    </div>
                                    {/* Product images upload */}
                                    {['image1', 'image2', 'image3', 'image4'].map((imageKey, index) => (
                                        <div className="col-md-6 col-lg-3" key={imageKey}>
                                            <div className="border rounded-0 p-3" style={{ background: '#ffffff', borderColor: '#cbd5e1' }}>
                                                <label className="form-label fw-bold mb-2" style={{ fontSize: '12px', color: '#374151' }}>
                                                    Image {index + 1}
                                                </label>
                                                <div className="text-center mb-3">
                                                    {uploadingStates[imageKey] ? (
                                                        <div className="d-flex align-items-center justify-content-center" style={{ height: '120px' }}>
                                                            <FaSpinner className="spinner-border spinner-border-sm me-2 text-primary" role="status" style={{ border: 'none', background: 'none' }} />
                                                            <small className="text-muted">Uploading...</small>
                                                        </div>
                                                    ) : (
                                                        <img
                                                            src={`${API_BASE_URL}/${productData?.[imageKey]}`}
                                                            alt={`${productData?.name} - Image ${index + 1}`}
                                                            className="img-fluid rounded-0"
                                                            style={{ maxHeight: '120px', width: 'auto', border: '1px solid #e2e8f0' }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'block';
                                                            }}
                                                        />
                                                    )}
                                                    <div className="text-muted small mt-1" style={{ display: 'none', fontSize: '11px' }}>
                                                        No image available
                                                    </div>
                                                </div>
                                                <input
                                                    type="file"
                                                    className="form-control rounded-0"
                                                    style={{ border: '1px solid #cbd5e1', fontSize: '13px', padding: '7px 12px' }}
                                                    name={imageKey}
                                                    accept="image/*"
                                                    onChange={handleReplaceImage}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    {/* Product video upload (optional) */}
                                    <div className="col-md-6 col-lg-3" key="video">
                                        <div className="border rounded-0 p-3" style={{ background: '#ffffff', borderColor: '#cbd5e1' }}>
                                            <label className="form-label fw-bold mb-2" style={{ fontSize: '12px', color: '#374151' }}>Product Video (optional)</label>
                                            <div className="text-center mb-3" style={{ minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                {productData?.video ? (
                                                    <small className="text-success d-block fw-bold">✓ Video uploaded</small>
                                                ) : (
                                                    <small className="text-muted d-block" style={{ fontSize: '11px' }}>No video uploaded</small>
                                                )}
                                                {uploadingStates.video && (
                                                    <div className="d-flex align-items-center justify-content-center mt-2">
                                                        <FaSpinner className="spinner-border spinner-border-sm me-2 text-primary" role="status" style={{ border: 'none', background: 'none' }} />
                                                        <small className="text-muted">Uploading...</small>
                                                    </div>
                                                )}
                                            </div>
                                            <input
                                                type="file"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', fontSize: '13px', padding: '7px 12px' }}
                                                name="video"
                                                accept="video/*"
                                                onChange={handleReplaceVideo}
                                            />
                                            <small className="text-muted d-block mt-1" style={{ fontSize: '10px' }}>MP4/WebM/OGG, max 50MB</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="row g-0 mt-4">
                    <div className="col-12 d-flex justify-content-end gap-3" style={{ padding: 0 }}>
                        <button
                            className="btn btn-outline-secondary rounded-0"
                            style={{ height: '38px', fontSize: '13px', padding: '0 20px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => navigate(-1)}
                            type="button"
                        >
                            Cancel
                        </button>
                        <button
                            className="btn theme-btn-blue rounded-0"
                            style={{ height: '38px', fontSize: '13px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                            onClick={handleUpdateProduct}
                            disabled={saving}
                            type="button"
                        >
                            {saving ? (
                                <><FaSpinner className="spinner-border spinner-border-sm" role="status" style={{ border: 'none', background: 'none' }} /> Updating...</>
                            ) : (
                                <><FaSave size={13} /> Update Product</>
                            )}
                        </button>
                    </div>
                </div>
                {/* Spacer to provide blank space at the bottom */}
                <div style={{ height: '60px' }}></div>
            </div>
        </Fragment>
    );
}

export default EditProductPage;
